import React from "react";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";

import { getRoleDisplayName } from "src/adminPanel/shared/roleCatalog";
import { Button } from "src/design-system/Button";
import { toastActions } from "src/design-system/Toast/utils";
import {
  useTokenFlowFolderScope,
  useTokenRoleScope,
} from "src/store/AuthStore";
import { useSetRoleScopeMutation } from "src/store/authQueries";
import { pluralize } from "src/utils/stringUtils";

const describeScope = (roles: string[], flowFolderIds?: string[]): string => {
  const named =
    roles.length <= 2
      ? roles.map(getRoleDisplayName).join(", ")
      : `${roles.length} roles`;
  return flowFolderIds?.length
    ? `${named} in ${pluralize(flowFolderIds.length, "folder")}`
    : named;
};

/**
 * Persistent reminder that the session token is impersonating a subset of the
 * user's roles, with a one-click way out. Distinct from RoleOverrideIndicator,
 * which reflects the UI-only CMD+K role switch.
 */
export const TokenImpersonationIndicator: React.FC = () => {
  const scopeRoles = useTokenRoleScope();
  const scopeFolders = useTokenFlowFolderScope();
  const { mutate: setRoleScope, isPending: isClearing } =
    useSetRoleScopeMutation();

  if (!scopeRoles) {
    return null;
  }

  const handleClear = () =>
    setRoleScope(
      { roles: [] },
      {
        onError: () =>
          toastActions.failure({
            title: "Could not restore your roles",
            description: "Log out and back in to drop the impersonation.",
          }),
      },
    );

  return (
    <div className="fixed bottom-14 left-1/2 z-50 -translate-x-1/2 transform">
      <Button
        dataLoc="token-impersonation-indicator"
        iconLeft={faTimes}
        isLoading={isClearing}
        variant="warning"
        onClick={handleClear}
      >
        Remove token impersonation: {describeScope(scopeRoles, scopeFolders)}
      </Button>
    </div>
  );
};
